import React, { FC, useContext, useState, useEffect, useMemo } from 'react';
import cloneDeep from 'lodash/cloneDeep';
import find from 'lodash/find';
import get from 'lodash/get';
import moment from 'moment-timezone';
import produce from 'immer';
import set from 'lodash/set';
import styled from 'styled-components';
import { useDispatch } from 'react-redux';

import Dropdown from 'src/presentation/components/forms/Dropdown';
import FormContext from 'src/presentation/contexts/FormContext';
import NodeSelector from 'src/presentation/components/pages/ivr/components/NodeSelector';
import ivrResource from 'src/actions/ivrs/ivrResource';
import useCreateNew from 'src/presentation/components/pages/ivr/hooks/useCreateNew';
import useDeleteTemporarySplit from 'src/presentation/components/pages/ivr/hooks/useDeleteTemporarySplit';
import useSaveTemporarySplit from 'src/presentation/components/pages/ivr/hooks/useSaveTemporarySplit';
import { Button } from 'src/presentation/components/agent_desktop_components';
import { ButtonTypes } from '@ad-scripts/presentation/components/common/button';
import { ContainerForOptions, FilledWrapper, Row } from 'src/presentation/components/pages/ivr/layouts/IvrLayout';
import { IvrEditorContext } from 'src/presentation/components/pages/ivr/IvrEditor';
import { ScheduleShortPresentation } from './components/ScheduleShortPresentation';
import { TIMEZONE_OPTIONS } from 'src/presentation/components/pages/shared/constants/dateTimeConstants';
import {
  SHORT_VIEW_TIME_FORMAT,
  SHORT_VIEW_DATE_FORMAT,
  SHORT_VIEW_DATETIME_FORMAT,
} from 'src/presentation/components/pages/shared/constants/dateTimeConstants';
import {
  getIndefinitelySchedule,
  getRegularSchedule,
  getTimeRangeForTemporarySplitNode,
} from 'src/presentation/components/pages/ivr/components/helpers/temporarySplitNodeHelpers';
import { NewNodeType } from '../../hooks/useCreateNewNode';
import { IvrTemplateType } from '../../constants/templates';
import ivrsResource from 'src/actions/ivrs/ivrsResource';
import { getConfigurationFromTemplate } from '../ivrTree/helpers/newNodesHelper';
import { useResourceValue } from 'src/presentation/hooks/useDocumentResource';
import ivrsTreeDataResource from 'src/actions/ivrs/ivrsTreeDataResource';
import organizationResource from 'src/actions/organization/organizationResource';
import { IvrConfig } from '../ivrTree/config/ivrConfig';
import { calculateNodesPositionsForTemplate } from '../ivrTree/helpers/nodesHelper';
import { forEach } from 'lodash';

const useSchedule = ({ attr }) => {
  const { workingState } = useContext(FormContext);

  return useMemo(() => {
    const schedules = get(workingState, attr);
    return getTimeRangeForTemporarySplitNode(schedules);
  }, [attr, workingState]);
};

const timeRangeOptions = [
  { label: 'Indefinitely', value: 'Indefinitely' },
  { label: '30 minutes', value: '30' },
  { label: '45 minutes', value: '45' },
  { label: '60 minutes', value: '60' },
  { label: '2 hours', value: '120' },
  { label: '3 hours', value: '180' },
  { label: '4 hours', value: '240' },
  { label: '5 hours', value: '300' },
  { label: '6 hours', value: '360' },
  { label: '7 hours', value: '420' },
  { label: '8 hours', value: '480' },
  { label: '24 hours', value: '1440' },
];

type AddTemporarySplitProps = {
  attr: string;
  'data-aid': string;
  disabled?: boolean;
  nodeList: any;
  onSelectNode?: Function;
  otherProps?: any;
  readOnly?: boolean;
};

export const AddTemporarySplit: FC<AddTemporarySplitProps> = ({
  attr,
  'data-aid': dataAid,
  disabled,
  nodeList,
  onSelectNode,
  readOnly,
  ...otherProps
}) => {
  const { setFormIsValid } = useContext(IvrEditorContext);
  const dispatch = useDispatch();
  const { validateSchedule, validateConfiguration } = ivrResource.effects;
  const { workingState } = useContext(FormContext);

  const { newNode, options, onCreateNewNode, onCreateNewNodesFromTemplate } = useCreateNew({ nodeList });

  const baseAttr = `${attr}.nodeSchedules`;
  const localTz = moment.tz.guess();
  const item = get(workingState, attr);
  const defaultOffId = get(item, 'defaultOffId', '');
  const currentNodeId = get(workingState, `${attr}.id`);

  const [value, setValue] = useState({ time: '30', nextId: defaultOffId, location: localTz });
  const { durationInMinutes, end, isNew, location, nextId, start, theSameDay } = useSchedule({ attr: baseAttr });
  const [editMode, setEditMode] = useState(isNew);

  useEffect(() => {
    setValue({ time: durationInMinutes, nextId: nextId || defaultOffId, location });
  }, [defaultOffId, durationInMinutes, location, nextId]);

  const onSelectLocation = e => {
    setValue({ ...value, location: e });
  };

  const onSelectTime = e => {
    setValue({ ...value, time: e });
  };

  const saveTemporarySplit = useSaveTemporarySplit(attr, value.nextId, newNode, onSelectNode);

  const { value: positions } = useResourceValue(ivrsTreeDataResource);
  const { value: organization } = useResourceValue(organizationResource);

  const saveScheduleWithTemplateHandler = schedules => {
    const configurationFromTemplate = getConfigurationFromTemplate(schedules[0].nodeId, organization);

    const parentId = get(workingState, `${attr}.id`);

    const templateConfig = new IvrConfig(configurationFromTemplate);
    const templateNodesLocations = calculateNodesPositionsForTemplate({
      locationState: positions,
      parentId,
      templateConfig,
    });

    forEach(schedules, schedule => {
      schedule.nodeId = templateConfig.rootNode.ivrNode.id;
    });

    const state = produce<any>(workingState, draft => {
      if (isNew) {
        draft.nodesMap = { ...draft.nodesMap, ...templateConfig.nodesMap };
      }
      set(draft, `${attr}.defaultOffId`, templateConfig.rootNode.ivrNode.id);
      set(draft, baseAttr, schedules);
    });

    dispatch(
      ivrsResource.effects.updateWithCoordinates({
        config: state,
        positions: { ...positions, ...templateNodesLocations },
      })
    );
    setEditMode(false);
  };

  const onSubmit = () => {
    const nodeSchedules: any =
      value.time === 'Indefinitely' ? getIndefinitelySchedule(value) : getRegularSchedule(value);

    const isValid = dispatch(validateSchedule(nodeSchedules[0]));

    setFormIsValid(get(item, 'id'), !!isValid);

    if (Object.values(IvrTemplateType).includes(nodeSchedules[0].nodeId as IvrTemplateType)) {
      saveScheduleWithTemplateHandler(nodeSchedules);
      return;
    }

    const state = produce(workingState, draft => {
      const isNew = !get(draft, attr);

      if (isNew) {
        const schedules: any[] = get(draft, baseAttr, []);
        schedules.push(nodeSchedules);
      } else {
        set(draft, `${baseAttr}`, nodeSchedules);
      }
    });

    dispatch(validateConfiguration(state));

    if (!isValid) {
      return;
    }

    saveTemporarySplit(nodeSchedules);

    setEditMode(false);
  };

  const deleteTemporarySplit = useDeleteTemporarySplit(baseAttr);

  const shortPresentationContext = getShortPresentationContext(start, end, theSameDay);

  const renderShortPresentation = () => {
    return (
      <ShortPresentationContainer>
        <FilledWrapper data-aid={`${dataAid}-extendedView-shortPresentation`}>
          <ScheduleShortPresentation
            context={shortPresentationContext}
            data-aid={`${dataAid}-extendedView-shortPresentation`}
            disabled={readOnly || disabled}
            node={find(nodeList, item => item.value === nextId)}
            onDelete={deleteTemporarySplit}
            onSelectNode={onSelectNode}
            onSwitchToEditMode={() => setEditMode(true)}
            timeZone={value.location}
          />
        </FilledWrapper>

        <RequirePublishInfoRow>This will only take into effect when you publish this version.</RequirePublishInfoRow>
      </ShortPresentationContainer>
    );
  };

  const renderLongPresentation = () => {
    const onNextIdChange = event => {
      let val = get(event, 'target.value', event);
      if (typeof val !== 'string') {
        if (val.type === NewNodeType.NODE) {
          onCreateNewNode(cloneDeep(val.node));
          val = val.node.ivrNode.id;
        } else {
          onCreateNewNodesFromTemplate(val.templateId);
          val = val.templateId;
        }
      }
      setValue({ ...value, nextId: val });
    };

    return (
      <FilledWrapper data-aid={`${dataAid}-extendedView`}>
        <Row>
          <Dropdown
            searchable
            data-aid={`${dataAid}-extendedView-timezone`}
            disabled={disabled}
            label="Timezone"
            onSelect={onSelectLocation}
            options={TIMEZONE_OPTIONS}
            readOnly={readOnly}
            value={value.location}
          />
        </Row>
        <Row>
          <Dropdown
            data-aid={`${dataAid}-extendedView-timeRange`}
            description="Close the IVR to split to another path. It will auto-reopen after this period."
            disabled={disabled}
            label=""
            onSelect={onSelectTime}
            options={timeRangeOptions}
            readOnly={readOnly}
            value={value.time}
            {...otherProps}
          />
        </Row>
        <Row>
          <NodeSelector
            allowEmptyValues
            attr={`${attr}.nodeSchedules[0].nodeId`}
            data-aid={`${dataAid}-extendedView-nextNode`}
            description="Next node in the IVR"
            disabled={disabled}
            label="Next node"
            omittedNodeIds={[currentNodeId]}
            onChange={onNextIdChange}
            options={options}
            searchable
            stayOnParentNodeAfterCreation
            value={value.nextId}
          />
        </Row>
        <ActionRow>
          {!isNew && (
            <Button
              data-aid={`${dataAid}-extendedView-cancel`}
              type={ButtonTypes.TEXT}
              onClick={() => setEditMode(false)}
            >
              Cancel
            </Button>
          )}
          <Button
            data-aid={`${dataAid}-extendedView-ok`}
            disabled={disabled}
            onClick={onSubmit}
            type={ButtonTypes.SECONDARY}
          >
            Save
          </Button>
        </ActionRow>
      </FilledWrapper>
    );
  };
  const showShortPresentation = !isNew && !editMode;
  return (
    <ContainerForOptions
      disabled={readOnly || disabled}
      label="Temporary Split"
      context={showShortPresentation ? renderShortPresentation() : renderLongPresentation()}
    />
  );
};

export const getShortPresentationContext = (
  start: moment.Moment | null,
  end: moment.Moment | null,
  theSameDay: boolean
) => {
  let shortDescription;
  if (!start || !end) {
    shortDescription = <>&#8734; Indefinitely</>;
  } else if (theSameDay) {
    shortDescription = `${start.format(SHORT_VIEW_DATE_FORMAT)} ${start.format(SHORT_VIEW_TIME_FORMAT)} - ${end.format(
      SHORT_VIEW_TIME_FORMAT
    )}`;
  } else {
    shortDescription = `${start.format(SHORT_VIEW_DATETIME_FORMAT)} - ${end.format(SHORT_VIEW_DATETIME_FORMAT)}`;
  }

  return <div>{shortDescription}</div>;
};

const ActionRow = styled(Row)`
  justify-content: center;
`;

const RequirePublishInfoRow = styled(Row)`
  font-style: italic;
  line-height: 162%;
`;

const ShortPresentationContainer = styled.div``;
